/**
 * P79-6 — Command palette kernel dispatch outside BusinessWorkbenchRuntimeProvider.
 * Focus bridge → CustomEvent → shell listener (open detail · inline edit).
 */

import type { NavigateFunction } from 'react-router-dom';
import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import type { WorkbenchKernelDispatchDeps } from '../components/workbench/ActionExecutor';
import {
  objectRefToCommandEntity,
  resolveActiveObjectRef,
} from '../hooks/workbench/useWorkbenchCommandContext';
import { getActiveWorkbenchCommandFocus } from './workbench-command-focus-bridge';

export const WORKBENCH_PALETTE_OPEN_DETAIL_EVENT = 'inflomatrix:workbench-palette-open-detail';
export const WORKBENCH_PALETTE_ENTER_EDIT_EVENT = 'inflomatrix:workbench-palette-enter-edit';

export interface WorkbenchPaletteObjectDetail {
  objectRef: ObjectRef;
}

export function emitWorkbenchPaletteOpenDetail(objectRef: ObjectRef): void {
  if (typeof window === 'undefined') {
    return;
  }
  const detail: WorkbenchPaletteObjectDetail = { objectRef };
  window.dispatchEvent(
    new CustomEvent<WorkbenchPaletteObjectDetail>(WORKBENCH_PALETTE_OPEN_DETAIL_EVENT, { detail })
  );
}

export function emitWorkbenchPaletteEnterEdit(objectRef: ObjectRef): void {
  if (typeof window === 'undefined') {
    return;
  }
  const detail: WorkbenchPaletteObjectDetail = { objectRef };
  window.dispatchEvent(
    new CustomEvent<WorkbenchPaletteObjectDetail>(WORKBENCH_PALETTE_ENTER_EDIT_EVENT, { detail })
  );
}

/** Builds kernel deps for GlobalSearch when workbench focus is published via bridge. */
export function resolvePaletteKernelDeps(
  tenantId: string,
  navigate: NavigateFunction
): WorkbenchKernelDispatchDeps | undefined {
  const focus = getActiveWorkbenchCommandFocus();
  if (!focus) {
    return undefined;
  }

  const objectRef = resolveActiveObjectRef(focus.root, focus.selection);
  if (!objectRef?.id) {
    return undefined;
  }

  const entitySnapshot = objectRefToCommandEntity(objectRef, tenantId);
  const selectedRefs = focus.selection.length > 0 ? [...focus.selection] : [objectRef];

  return {
    tenantId,
    objectRef,
    entitySnapshot,
    selectionCount: selectedRefs.length,
    selectedRefs,
    flowStartContext: {
      entityId: entitySnapshot.id,
      businessDomain: String(entitySnapshot.businessDomain ?? objectRef.businessDomain),
    },
    navigate,
    onOpenDetail: () => emitWorkbenchPaletteOpenDetail(objectRef),
    onEnterEditMode: () => emitWorkbenchPaletteEnterEdit(objectRef),
    onStartFlow: (_flowTemplateId) => navigate('/workspace'),
  };
}
